import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { NAV_LINKS, RESOURCE_LINKS, SITE } from '../lib/site';

const ResourcesMenu = ({ open, onNavigate }) => (
  <div
    role="menu"
    aria-label="Resources"
    data-testid="nav-resources-menu"
    className={`absolute right-0 top-full pt-3 transition-all duration-200 ${
      open ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0 pointer-events-none'
    }`}
  >
    <div className="w-60 overflow-hidden rounded-2xl border border-brand-line bg-white p-2 shadow-lift">
      {RESOURCE_LINKS.map((r) => (
        <Link
          key={r.to}
          role="menuitem"
          to={r.to}
          onClick={onNavigate}
          className="block rounded-xl px-3.5 py-2.5 text-sm font-semibold text-brand-body transition-colors hover:bg-brand-ice hover:text-brand-navy"
        >
          {r.label}
        </Link>
      ))}
    </div>
  </div>
);

export const Navbar = () => {
  const [dropOpen, setDropOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [mobileRes, setMobileRes] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setDropOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setDropOpen(false);
    };
    document.addEventListener('pointerdown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    setMobile(false);
    setDropOpen(false);
    setMobileRes(false);
  }, [pathname]);

  const openedByHover = useRef(false);

  const hoverOpen = () => {
    clearTimeout(closeTimer.current);
    openedByHover.current = true;
    setDropOpen(true);
  };
  const hoverClose = () => {
    closeTimer.current = setTimeout(() => {
      setDropOpen(false);
      openedByHover.current = false;
    }, 120);
  };
  const toggleResources = () => {
    if (openedByHover.current) {
      openedByHover.current = false;
      return;
    }
    setDropOpen((o) => !o);
  };

  const linkCls = ({ isActive }) =>
    `rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
      isActive ? 'text-brand-skyDeep' : 'text-brand-body hover:text-brand-navy'
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'border-b border-slate-100 bg-white/85 shadow-soft backdrop-blur-md' : 'bg-white/60 backdrop-blur-sm'
      }`}
      data-testid="site-header"
    >
      <nav className="container-x flex h-[72px] items-center justify-between gap-4" aria-label="Main navigation">
        <Link to="/" aria-label="CapableBee home" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={linkCls}
              data-testid={`nav-link-${l.to === '/' ? 'home' : l.to.slice(1)}`}
            >
              {l.label}
            </NavLink>
          ))}

          <div ref={wrapRef} className="relative" onMouseEnter={hoverOpen} onMouseLeave={hoverClose}>
            <button
              type="button"
              aria-expanded={dropOpen}
              aria-haspopup="menu"
              data-testid="nav-resources-trigger"
              onClick={toggleResources}
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-brand-body transition-colors hover:text-brand-navy"
            >
              Resources
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${dropOpen ? 'rotate-180 text-brand-skyDeep' : ''}`}
              />
            </button>
            <ResourcesMenu open={dropOpen} onNavigate={() => setDropOpen(false)} />
          </div>
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <a
            href={SITE.loginUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-login-link"
            className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-bold text-brand-navy shadow-card transition-all hover:-translate-y-0.5 hover:border-brand-lineBlue hover:shadow-lift"
          >
            Login
          </a>
          <Link
            to="/contact"
            data-testid="nav-get-started-btn"
            className="rounded-full bg-brand-sky px-5 py-2.5 text-sm font-bold text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-brand-skyDeep hover:shadow-lift"
          >
            Get Started
          </Link>
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-brand-line bg-white text-brand-navy lg:hidden"
          aria-expanded={mobile}
          aria-label={mobile ? 'Close menu' : 'Open menu'}
          data-testid="mobile-menu-button"
          onClick={() => setMobile((m) => !m)}
        >
          {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <div
        className={`overflow-hidden border-b border-slate-100 bg-white transition-all duration-300 lg:hidden ${
          mobile ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="container-x space-y-1 py-4">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="block rounded-xl px-4 py-3 text-base font-semibold text-brand-navy hover:bg-brand-ice"
              data-testid={`mobile-link-${l.to === '/' ? 'home' : l.to.slice(1)}`}
            >
              {l.label}
            </Link>
          ))}

          <button
            type="button"
            aria-expanded={mobileRes}
            data-testid="mobile-resources-trigger"
            onClick={() => setMobileRes((r) => !r)}
            className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-base font-semibold text-brand-navy hover:bg-brand-ice"
          >
            Resources
            <ChevronDown
              className={`h-4 w-4 transition-transform ${mobileRes ? 'rotate-180 text-brand-skyDeep' : ''}`}
            />
          </button>
          {mobileRes && (
            <div className="ml-3 border-l-2 border-brand-lineBlue pl-3" data-testid="mobile-resources-menu">
              {RESOURCE_LINKS.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="block rounded-lg px-4 py-2.5 text-sm font-semibold text-brand-body hover:bg-brand-ice"
                  data-testid={`mobile-resource-${r.to.slice(1)}`}
                >
                  {r.label}
                </Link>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-3">
            <a
              href={SITE.loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-login-link"
              className="flex-1 rounded-full border border-brand-line bg-white px-5 py-3 text-center text-sm font-bold text-brand-navy"
            >
              Login
            </a>
            <Link
              to="/contact"
              data-testid="mobile-get-started-btn"
              className="flex-1 rounded-full bg-brand-sky px-5 py-3 text-center text-sm font-bold text-white"
            >
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};
